import { createServerFn } from "@tanstack/react-start";
import { generateText } from "ai";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { createLovableAiGateway } from "./ai-gateway.server";
import type { Database } from "@/integrations/supabase/types";

const HeadlineSchema = z.object({
  email: z.string().trim().email().max(254),
  current_headline: z.string().trim().min(1).max(400),
  role: z.string().trim().max(200).optional().default(""),
  audience: z.string().trim().max(300).optional().default(""),
  outcome: z.string().trim().max(300).optional().default(""),
  credentials: z.string().trim().max(200).optional().default(""),
});

export interface HeadlineResult {
  diagnosis: string;
  options: Array<{ label: string; headline: string; why: string }>;
  quick_wins: string[];
}

const SYSTEM_PROMPT = `You are a LinkedIn positioning expert who writes headlines for financial professionals (RIAs, advisors, bankers, fintech leaders).

Rules for every headline you write:
- 200 characters maximum, ideally 120-180.
- Lead with who they help and the outcome, not a job title alone.
- Plain, human language. No buzzwords, no hype, no "guru", no emojis, no em dashes.
- Use the pipe character | to separate clauses.
- Never invent credentials, firm names, or numbers the user did not provide.
- Compliance-safe: no performance promises, no guaranteed returns.

Return ONLY raw JSON, no markdown fences, in exactly this shape:
{"diagnosis":"","options":[{"label":"Clear and direct","headline":"","why":""},{"label":"Client outcome first","headline":"","why":""},{"label":"Referral ready","headline":"","why":""}],"quick_wins":["","",""]}

diagnosis: 2-3 sentences on what is weak about their current headline and why it costs them visibility in search and referrals.
why: one sentence per option.
quick_wins: 3 short, specific profile fixes beyond the headline. Make them clear and actionable. The third quick win must recommend adding specific, relevant keywords to the Experience section bullets, formatted like: "Use the words 'first-generation business owners', 'estate planning', and 'charitable planning' as keywords in your Experience section bullets." Choose keywords that fit the advisor's audience and services.`;

export const reviseHeadline = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => HeadlineSchema.parse(input))
  .handler(async ({ data }): Promise<HeadlineResult> => {
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("The headline tool is not configured right now.");
    const gateway = createLovableAiGateway(key);

    const prompt = `Current LinkedIn headline: ${data.current_headline}
Role / title: ${data.role || "(not provided)"}
Who they serve: ${data.audience || "(not provided)"}
Outcome they deliver: ${data.outcome || "(not provided)"}
Credentials or firm: ${data.credentials || "(not provided)"}

Rewrite this headline three ways and return the JSON described in your instructions.`;

    let text = "";
    try {
      const result = await generateText({
        model: gateway("openai/gpt-5.6-sol"),
        system: SYSTEM_PROMPT,
        prompt,
        providerOptions: { lovable: { reasoningEffort: "none" } },
      });
      text = result.text;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes("429")) {
        throw new Error("A lot of people are using this right now. Try again in a moment.");
      }
      if (message.includes("402")) {
        throw new Error("The headline tool is temporarily unavailable. Email katie@katiefernands.com and she will do it by hand.");
      }
      throw new Error("Could not rewrite your headline. Please try again.");
    }

    const cleaned = text
      .trim()
      .replace(/^```(?:json)?/i, "")
      .replace(/```$/i, "")
      .trim();

    try {
      const result = JSON.parse(cleaned) as HeadlineResult;
      void saveHeadlineSubmission(data, result).catch((err) =>
        console.warn("headline submission save failed", err),
      );
      void sendHeadlineEmails(data, result).catch((err) =>
        console.warn("headline results email failed", err),
      );
      return result;
    } catch {
      throw new Error("Could not read the response. Please try again.");
    }
  });

async function sendHeadlineEmails(
  data: z.infer<typeof HeadlineSchema>,
  result: HeadlineResult,
) {
  const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
  const stamp = Date.now();
  const templateData = {
    current_headline: data.current_headline,
    diagnosis: result.diagnosis,
    options: result.options,
    quick_wins: result.quick_wins,
  };

  await Promise.allSettled([
    sendTemplateEmail("headline-results", data.email, {
      templateData,
      idempotencyKey: `headline-results-${data.email}-${stamp}`,
      from: "Katie Fernands <katie@katiefernands.com>",
      replyTo: "katie@katiefernands.com",
    }),
    sendTemplateEmail("headline-results", "kfernands1@gmail.com", {
      templateData: { ...templateData, is_copy: true, recipient_email: data.email },
      idempotencyKey: `headline-results-copy-${data.email}-${stamp}`,
      replyTo: data.email,
    }),
  ]);
}

async function saveHeadlineSubmission(
  data: z.infer<typeof HeadlineSchema>,
  result: HeadlineResult,
) {
  const url = process.env.SUPABASE_URL!;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  const supabase = createClient<Database>(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
  });

  await supabase.from("headline_submissions").insert({
    email: data.email,
    current_headline: data.current_headline,
    role: data.role || null,
    audience: data.audience || null,
    outcome: data.outcome || null,
    credentials: data.credentials || null,
    diagnosis: result.diagnosis,
    options: result.options,
    quick_wins: result.quick_wins,
  });
}
